export const FISCAL_COMMITTEE_CODES = { Assembly: 'CX25', Senate: 'CS61' };
export const BUDGET_COMMITTEE_CODES = { Assembly: 'CX29', Senate: 'CS62' };
export const BUDGET_COMMITTEE_AUTHOR_NAMES = ['Committee on Budget', 'Committee on Budget and Fiscal Review'];
const TERMINAL_STATUSES = ['Died', 'Vetoed', 'Failed', 'Failed Passage in Committee'];
const SUCCESS_TERMINAL_STATUSES = ['Chaptered', 'Signed by Governor'];

export function isBudgetBill(latestVersion) {
  return (latestVersion?.BillVersionAuthors || []).some(
    (a) => a.author_type === 'Committee' && BUDGET_COMMITTEE_AUTHOR_NAMES.includes(a.author_name)
  );
}

function classifyRow(row, house) {
  const otherHouse = house === 'Assembly' ? 'Senate' : 'Assembly';
  const chamber = row.primary_location === house ? 'own' : row.primary_location === otherHouse ? 'other' : null;

  if (/concurren/i.test(row.action || '')) {
    return { type: 'concurrence', chamber: 'own' };
  }
  if (row.primary_location === 'Governor' || row.primary_location === 'Secretary of State') {
    return { type: 'governor', chamber: null };
  }
  if (/introduc/i.test(row.action || '')) {
    return { type: 'introduction', chamber: null };
  }
  if (row.secondary_location === 'Committee' && chamber) {
    if (row.ternary_location === BUDGET_COMMITTEE_CODES[chamber === 'own' ? house : otherHouse]) {
      return { type: 'budget', chamber };
    }
    if (row.ternary_location === FISCAL_COMMITTEE_CODES[chamber === 'own' ? house : otherHouse]) {
      return { type: 'fiscal', chamber };
    }
    return { type: 'policy', chamber };
  }
  if (row.secondary_location === 'Floor' && chamber) {
    return { type: 'floor', chamber };
  }
  return { type: null, chamber };
}

function buildStepDefs(budgetBill) {
  if (budgetBill) {
    return [
      { key: 'introduction', label: 'Introduction', chamber: null, match: (r) => r.type === 'introduction' },
      { key: 'budget_own', label: 'Budget Committee', chamber: 'own', match: (r) => r.type === 'budget' && r.chamber === 'own' },
      { key: 'floor_own', label: 'Floor', chamber: 'own', match: (r) => r.type === 'floor' && r.chamber === 'own' },
      { key: 'budget_other', label: 'Budget Committee', chamber: 'other', match: (r) => r.type === 'budget' && r.chamber === 'other' },
      { key: 'floor_other', label: 'Floor', chamber: 'other', match: (r) => r.type === 'floor' && r.chamber === 'other' },
      { key: 'concurrence', label: 'Concurrence', chamber: 'own', match: (r) => r.type === 'concurrence', optional: true },
      { key: 'enrollment', label: 'Enrollment', chamber: null, match: (r) => r.ternary_location === 'Enrollment', optional: true },
      { key: 'governor', label: 'Action by Governor', chamber: null, match: (r) => r.type === 'governor' },
    ];
  }
  return [
    { key: 'introduction', label: 'Introduction', chamber: null, match: (r) => r.type === 'introduction' },
    { key: 'policy_own', label: 'Policy Committee', chamber: 'own', match: (r) => r.type === 'policy' && r.chamber === 'own' },
    { key: 'fiscal_own', label: 'Fiscal Committee (Appropriations)', chamber: 'own', match: (r) => r.type === 'fiscal' && r.chamber === 'own' },
    { key: 'floor_own', label: 'Floor', chamber: 'own', match: (r) => r.type === 'floor' && r.chamber === 'own' },
    { key: 'policy_other', label: 'Policy Committee', chamber: 'other', match: (r) => r.type === 'policy' && r.chamber === 'other' },
    { key: 'fiscal_other', label: 'Fiscal Committee (Appropriations)', chamber: 'other', match: (r) => r.type === 'fiscal' && r.chamber === 'other' },
    { key: 'floor_other', label: 'Floor', chamber: 'other', match: (r) => r.type === 'floor' && r.chamber === 'other' },
    { key: 'concurrence', label: 'Concurrence', chamber: 'own', match: (r) => r.type === 'concurrence', optional: true },
    { key: 'enrollment', label: 'Enrollment', chamber: null, match: (r) => r.ternary_location === 'Enrollment', optional: true },
    { key: 'governor', label: 'Action by Governor', chamber: null, match: (r) => r.type === 'governor' },
  ];
}

export function classifyBillLifecycle({ house, status, billHistory, isBudget }) {
  if (!house || !Array.isArray(billHistory)) return [];

  const sorted = [...billHistory].sort((a, b) => {
    const seqA = a.action_sequence ?? 0;
    const seqB = b.action_sequence ?? 0;
    if (seqA !== seqB) return seqA - seqB;
    return new Date(a.action_date) - new Date(b.action_date);
  });

  const classified = sorted.map((row) => ({ ...row, ...classifyRow(row, house) }));
  const stepDefs = buildStepDefs(isBudget);

  // Every bill has an introduction date, whether or not any row's action text says "Introduced" —
  // fall back to the earliest history row so this step is never left unmatched (and therefore never "skipped").
  const introductionDate = (classified.find((r) => r.type === 'introduction') || classified[0])?.action_date || null;

  const steps = stepDefs
    .map((def) => {
      if (def.key === 'introduction') {
        return { key: def.key, label: def.label, chamber: def.chamber, optional: false, enteredDate: introductionDate };
      }
      const match = classified.find((r) => def.match(r));
      return {
        key: def.key,
        label: def.label,
        chamber: def.chamber,
        optional: !!def.optional,
        enteredDate: match ? match.action_date : null,
      };
    })
    .filter((step) => !step.optional || step.enteredDate);

  let lastCompletedIdx = -1;
  steps.forEach((step, idx) => {
    if (step.enteredDate) lastCompletedIdx = idx;
  });

  const isTerminal = TERMINAL_STATUSES.includes(status);
  const isSuccessComplete = SUCCESS_TERMINAL_STATUSES.includes(status);

  return steps.map((step, idx) => {
    let stepStatus;
    if (step.enteredDate) {
      if (idx === lastCompletedIdx) {
        stepStatus = isTerminal ? 'terminal' : isSuccessComplete ? 'success' : 'current';
      } else {
        stepStatus = 'complete';
      }
    } else if (idx < lastCompletedIdx) {
      // A later step already has a date, so this stage was skipped rather than not-yet-reached
      stepStatus = 'skipped';
    } else {
      stepStatus = 'upcoming';
    }
    return { ...step, status: stepStatus };
  });
}
